import React, { useMemo } from "react";

import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { ValidationRow } from "models";

import { ChartTooltipProps } from "pages/admin/soilSight/utils/compareTypes.ts";
import { getScenarioColor } from "pages/admin/soilSight/utils/compareUtils.ts";

type CropDatum = { crop: string } & Record<string, number | string | null>;

type CustomTooltipProps = ChartTooltipProps<string>;

const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="soil-sight-tooltip">
      <div className="soil-sight-tooltip-year">{label}</div>
      {payload.map((entry) => (
        <div key={entry.name} className="soil-sight-tooltip-row">
          <span className="soil-sight-tooltip-dot" style={{ background: entry.color }} />
          <span className="soil-sight-tooltip-name">{entry.name}</span>
          <span className="soil-sight-tooltip-value">{entry.value != null ? Number(entry.value).toFixed(2) : "—"}</span>
        </div>
      ))}
    </div>
  );
};

type Props = { rows: ValidationRow[]; location: string; rotation: string; scenarios: string[] };

export const YieldByCropChart = ({ rows, location, rotation, scenarios }: Props) => {
  const chartData = useMemo<CropDatum[]>(() => {
    const scoped = rows.filter((r) => r.location === location && r.rotation === rotation);

    // Crop order follows the rotation's own harvest schedule (first scenario found), not alphabetical —
    // the schedule is a function of the rotation, not the scenario, so any one scenario's row order works.
    const cropOrder: string[] = [];
    const seen = new Set<string>();
    for (const row of [...scoped].sort((a, b) => a.year - b.year)) {
      const { crop } = row;
      if (typeof crop === "string" && crop && !seen.has(crop)) {
        seen.add(crop);
        cropOrder.push(crop);
      }
    }

    return cropOrder.map((crop) => {
      const point: CropDatum = { crop };
      for (const scenario of scenarios) {
        const values = scoped
          .filter((r) => r.crop === crop && r.scenario === scenario)
          .map((r) => r.ss_cropYieldFractionOfTarget ?? null)
          .filter((v): v is number => v !== null);
        point[scenario] = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : null;
      }
      return point;
    });
  }, [rows, location, rotation, scenarios]);

  if (chartData.length === 0) {
    return <div className="soil-sight-chart-description">No per-year crop labels on this dataset (v3/v4 runs don't report them) — nothing to group by.</div>;
  }

  return (
    <div className="soil-sight-chart-card" style={{ gridColumn: "1 / -1" }}>
      <div className="soil-sight-chart-card-header">
        Yield vs Target by Crop
        <span className="soil-sight-chart-unit-badge">score</span>
      </div>
      <div className="soil-sight-chart-description">
        Average yield-vs-target across all harvested cycles of each crop, one bar per scenario — compares practice impact per crop, not per year.
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={chartData} margin={{ top: 4, right: 16, bottom: 0, left: -8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis dataKey="crop" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} domain={[0, "auto"]} />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            content={() => (
              <div className="soil-sight-charts-legend">
                {scenarios.map((scenario, i) => (
                  <span key={scenario} className="soil-sight-charts-legend-item">
                    <span className="soil-sight-charts-legend-swatch" style={{ background: getScenarioColor(scenario, i) }} />
                    {scenario}
                  </span>
                ))}
              </div>
            )}
          />
          {scenarios.map((scenario, i) => (
            <Bar key={scenario} dataKey={scenario} name={scenario} fill={getScenarioColor(scenario, i)} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
